import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Building2,
  CalendarCheck,
  Check,
  HeartHandshake,
  Home,
  MapPin,
  MessagesSquare,
  Phone,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { MaskedLine, Reveal } from "@/components/site/Reveal";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import { Marquee } from "@/components/site/Marquee";
import { ServiceCard } from "@/components/site/ServiceCard";
import { CTABanner } from "@/components/site/CTABanner";
import { Seo } from "@/components/site/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  BUSINESS,
  COMMERCIAL_SERVICES,
  FAQS,
  HOW_IT_WORKS,
  IMAGES,
  RESIDENTIAL_SERVICES,
  WHY_CHOOSE_US,
} from "@/lib/site";

const ICONS = { ShieldCheck, Sparkles, CalendarCheck, MessagesSquare, Building2, HeartHandshake };

const CATEGORIES = [
  {
    title: "Residential Cleaning",
    description: "Reliable cleaning for homes, rentals and short-term properties.",
    cta: "Explore Residential Cleaning",
    to: "/residential-cleaning",
    image: IMAGES.residential,
    alt: "Professional cleaner mopping a bright Sydney home",
    icon: Home,
  },
  {
    title: "Commercial & Industrial Cleaning",
    description: "Professional cleaning solutions for offices, buildings and commercial environments.",
    cta: "Explore Commercial Cleaning",
    to: "/commercial-cleaning",
    image: IMAGES.commercial,
    alt: "Pristine modern office space after professional cleaning",
    icon: Building2,
  },
  {
    title: "NDIS & Aged Care Cleaning",
    description: "Respectful home cleaning and maintenance support tailored to individual needs.",
    cta: "Learn More",
    to: "/ndis-aged-care-cleaning",
    image: IMAGES.care,
    alt: "Friendly home support in a bright, comfortable home",
    icon: HeartHandshake,
  },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 90]);
  const scale = useTransform(scrollY, [0, 700], [1, 1.1]);

  return (
    <section className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-28" data-testid="home-hero">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-emerald-50" />
      <div className="pointer-events-none absolute -left-32 top-64 h-96 w-96 rounded-full bg-sky-50" />

      <div className="container-site relative grid items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal y={14}>
            <span
              data-testid="hero-location-badge"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 font-heading"
            >
              <MapPin className="h-3.5 w-3.5" /> {BUSINESS.area}
            </span>
          </Reveal>

          <h1 className="mt-6 text-5xl font-bold tracking-tighter leading-[1.05] sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            <MaskedLine delay={0.1}>Professional Cleaning</MaskedLine>
            <MaskedLine delay={0.24}>Services You Can</MaskedLine>
            <MaskedLine delay={0.38}>
              Count <span className="text-emerald-500">On.</span>
            </MaskedLine>
          </h1>

          <Reveal delay={0.55}>
            <p className="mt-7 max-w-xl text-base leading-relaxed sm:text-lg" data-testid="hero-subtext">
              From regular home cleaning and deep cleans to end-of-lease, Airbnb, office, strata and
              specialised support cleaning — we help keep your space clean, fresh and ready for what
              matters, across {BUSINESS.area}.
            </p>
          </Reveal>

          <Reveal delay={0.68}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-quote-button" className="btn-primary group">
                Get a Free Quote
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a href={BUSINESS.phoneHref} data-testid="hero-call-button" className="btn-outline">
                <Phone className="h-4 w-4" /> Call Us
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.8}>
            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400" data-testid="hero-trust-line">
              Residential &nbsp;•&nbsp; Commercial &nbsp;•&nbsp; Short-Term Rentals &nbsp;•&nbsp; NDIS &amp; Aged Care
            </p>
          </Reveal>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y }} className="relative">
            <motion.div style={{ scale }}>
              <BeforeAfter
                before={IMAGES.heroBefore}
                after={IMAGES.heroAfter}
                beforeAlt="Real customer kitchen stovetop covered in built-up grease and grime before an Oz SpotNest clean"
                afterAlt="The same customer stovetop spotless and gleaming after an Oz SpotNest clean"
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-6 -left-4 sm:-left-8"
          >
            <div className="animate-float-slow flex items-center gap-3 rounded-2xl bg-white/90 p-4 pr-6 shadow-xl shadow-navy-900/10 backdrop-blur-xl ring-1 ring-slate-100">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <Sparkles className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-heading text-sm font-bold text-navy-800">{BUSINESS.tagline}</span>
                <span className="block text-xs text-slate-500">{BUSINESS.hours}</span>
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => (
  <>
    <Seo
      title="Oz SpotNest Cleaning Service | House, Office & Bond Cleaning Sydney NSW"
      description="Professional cleaning services across Sydney, NSW — regular domestic cleaning, deep cleaning, end-of-lease & bond cleaning, Airbnb turnovers, office & strata cleaning, NDIS & aged care support. Get a free quote."
    />
    <Hero />
    <Marquee
      items={[
        "Regular Domestic Cleaning",
        "Deep & Spring Cleaning",
        "End-of-Lease / Bond Cleaning",
        "Airbnb Turnovers",
        "Office Cleaning",
        "Strata & Building Cleaning",
        "NDIS Cleaning Support",
        "Aged Care Cleaning",
        `Serving ${BUSINESS.area}`,
      ]}
    />

    <section className="section-pad" data-testid="service-categories-section">
      <div className="container-site">
        <Reveal>
          <span className="eyebrow">What we clean</span>
          <h2 className="mt-4 max-w-2xl text-4xl font-bold tracking-tight sm:text-5xl">
            One team for every kind of space
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-7 md:grid-cols-3">
          {CATEGORIES.map((cat, i) => (
            <Reveal key={cat.title} delay={i * 0.1} className="h-full">
              <Link
                to={cat.to}
                data-testid={`category-card-${i}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl shadow-sky-900/5 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-sky-900/10"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/90 text-navy-800 shadow-lg backdrop-blur">
                    <cat.icon className="h-5 w-5" />
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="text-xl font-semibold tracking-tight">{cat.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed">{cat.description}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-heading text-sm font-bold text-emerald-600">
                    {cat.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="section-pad bg-slate-50" data-testid="residential-section">
      <div className="container-site">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <span className="eyebrow">For your home</span>
            <h2 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Residential Cleaning Services</h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed sm:text-lg">
              Whether you need regular help keeping your home tidy, a detailed deep clean, an
              end-of-lease clean or a fast Airbnb turnover, we offer flexible residential cleaning
              services to suit your property.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/residential-cleaning" data-testid="residential-explore-link" className="btn-navy group">
              Explore Residential
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
          {RESIDENTIAL_SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} compact />
          ))}
        </div>
      </div>
    </section>

    <section className="section-pad" data-testid="commercial-section">
      <div className="container-site">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <span className="eyebrow">For your business</span>
            <h2 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Commercial &amp; Industrial Cleaning</h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed sm:text-lg">
              Keep your workplace, building or commercial property clean, presentable and ready for
              staff, customers, residents and visitors.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/commercial-cleaning" data-testid="commercial-explore-link" className="btn-navy group">
              Explore Commercial
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-7 md:grid-cols-2">
          {COMMERCIAL_SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} compact />
          ))}
        </div>
      </div>
    </section>

    <section className="section-pad bg-navy-800 text-white" data-testid="care-section">
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="overflow-hidden rounded-[2.5rem] shadow-2xl shadow-black/30 ring-1 ring-white/10">
            <img
              src={IMAGES.care}
              alt="Respectful home cleaning support for NDIS participants and aged-care clients"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
        </Reveal>
        <div>
          <Reveal>
            <span className="eyebrow !text-emerald-400">Care &amp; support</span>
            <h2 className="mt-4 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              NDIS &amp; Aged Care Cleaning
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Respectful home cleaning and maintenance support tailored to individual needs — for
              NDIS participants, aged-care clients, families and care coordinators.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "General home cleaning, vacuuming and mopping",
                "Kitchen and bathroom cleaning",
                "Support shaped around individual preferences",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-200 sm:text-base">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" strokeWidth={3} />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/ndis-aged-care-cleaning" data-testid="care-learn-more-button" className="btn-white group">
                Learn More
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact?service=NDIS%20Cleaning"
                data-testid="care-discuss-button"
                className="btn border-2 border-white/25 px-7 py-3.5 text-white hover:border-white/60 hover:-translate-y-0.5"
              >
                Discuss Your Cleaning Needs
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="section-pad" data-testid="why-choose-us-section">
      <div className="container-site">
        <Reveal className="max-w-2xl">
          <span className="eyebrow">Why choose us</span>
          <h2 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Cleaning Done With Care</h2>
          <p className="mt-5 text-base leading-relaxed sm:text-lg">
            We're a new local business built on simple service principles — show up, do the job
            properly, and make the whole experience easy.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_CHOOSE_US.map((b, i) => {
            const Icon = ICONS[b.icon];
            return (
              <Reveal key={b.title} delay={i * 0.07} className="h-full">
                <div
                  data-testid={`benefit-card-${i}`}
                  className="h-full rounded-3xl border border-slate-100 bg-white p-8 shadow-lg shadow-sky-900/5 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 text-lg font-semibold tracking-tight">{b.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed">{b.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    <section className="section-pad bg-slate-50" data-testid="how-it-works-section">
      <div className="container-site">
        <Reveal className="max-w-2xl">
          <span className="eyebrow">How it works</span>
          <h2 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            From enquiry to spotless, in four steps
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {HOW_IT_WORKS.map((s, i) => (
            <Reveal key={s.step} delay={i * 0.1}>
              <div data-testid={`how-it-works-step-${i}`} className="relative">
                <span className="font-heading text-6xl font-extrabold tracking-tighter text-transparent [-webkit-text-stroke:1.5px_#10b981]">
                  {s.step}
                </span>
                <h3 className="mt-4 text-xl font-semibold tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed">{s.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-14">
            <Link to="/contact" data-testid="how-it-works-quote-button" className="btn-primary group">
              Start with a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="section-pad" data-testid="service-area-section">
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <span className="eyebrow">Service area</span>
          <h2 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            Cleaning Services Across {BUSINESS.area}
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed sm:text-lg">
            Based in Belmore NSW, we provide house cleaning, deep cleaning, end-of-lease and bond
            cleaning, Airbnb turnovers, office cleaning, strata cleaning and care-support cleaning
            for customers across Sydney.
          </p>
          <p className="mt-4 max-w-xl text-base leading-relaxed">
            Not sure whether we cover your area? Get in touch — if we can help, we'll tell you
            straight away, and if we can't, we won't waste your time.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["Homes", "Apartments", "Offices", "Strata Buildings", "Short-Term Rentals", "Care Support"].map(
              (chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-navy-800 font-heading"
                >
                  {chip}
                </span>
              )
            )}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="overflow-hidden rounded-[2.5rem] shadow-2xl shadow-sky-900/10 ring-1 ring-slate-100">
              <img
                src={IMAGES.bond}
                alt="Modern Sydney home cleaned and ready for its owners"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 left-6 rounded-2xl bg-white/90 px-5 py-4 shadow-xl backdrop-blur-xl ring-1 ring-slate-100">
              <p className="flex items-center gap-2 font-heading text-sm font-bold text-navy-800">
                <MapPin className="h-4 w-4 text-emerald-500" /> Based in Belmore · Serving Sydney, NSW
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="section-pad bg-slate-50" data-testid="faq-preview-section">
      <div className="container-site grid gap-12 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <span className="eyebrow">Common questions</span>
          <h2 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Frequently Asked Questions</h2>
          <p className="mt-5 text-base leading-relaxed sm:text-lg">
            Quick answers about our cleaning services, pricing and service area.
          </p>
          <Link to="/faq" data-testid="faq-view-all-link" className="btn-outline mt-8 group">
            View all FAQs
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-3">
          <Accordion type="single" collapsible className="w-full" data-testid="faq-preview-accordion">
            {FAQS.slice(0, 5).map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-slate-200">
                <AccordionTrigger className="text-left font-heading text-base font-semibold text-navy-800 hover:text-emerald-600 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>

    <CTABanner />
  </>
);

export default HomePage;
